import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import Icon, { IconName } from 'particles/Icon'
import IconButton from 'particles/Button/IconButton'
import { isOverflown } from './utils'
import Tooltip from './Tooltip'

export interface ChipProps extends React.ButtonHTMLAttributes<HTMLDivElement> {
    label: string
    icon?: IconName
    active?: boolean
    isDisabled?: boolean
    onDelete?: React.MouseEventHandler
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    maxWidth?: number
}

interface ChipContainerProp {
    isDisabled?: boolean
    active?: boolean
    maxWidth?: number
    isDeletable?: boolean
}

const ChipContainer = styled.div<ChipContainerProp>`
    position: relative;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    background-color: ${({ theme }): string => theme.colors.surface.primary};
    border: 1px solid
        ${({ theme, isDisabled, active }): string =>
            isDisabled
                ? theme.colors.border.disable
                : active
                  ? theme.colors.border.neutralActionActive
                  : theme.colors.border.neutralAction};
    border-radius: 40px;
    min-width: 120px;
    width: fit-content;
    max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : '240px')};
    height: 28px;
    padding: 3px 8px;
    gap: ${({ theme }) => theme.spacing[200]};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    ${({ isDisabled }) => isDisabled && `pointer-events: none;`}

    .content {
        display: grid;
        grid-template-columns: auto 1fr;
        width: ${({ isDeletable }) => (isDeletable ? 'calc(100% - 16px)' : '100%')};
        align-items: center;
        gap: ${({ theme }) => theme.spacing[100]};
        .label {
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    &:hover {
        border-color: ${({ theme }) => theme.colors.border.neutralAction};
        background-color: ${({ theme, isDisabled }) =>
            isDisabled ? theme.colors.surface.primary : theme.colors.surface.neutralActionSubPastelHover};
    }

    &:focus-visible {
        outline: 3px solid ${({ theme }) => theme.colors.border.neutralActionFocus};
    }
    .close-button {
        justify-self: flex-end;
    }
`

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
    ({ className, icon, label, isDisabled, onClick, onDelete, id, active = false, maxWidth }, ref) => {
        const deletable = !isDisabled && typeof onDelete === 'function'
        const [isLabelOverflowed, setIsLabelOverflowed] = useState(false)
        const textElementRef = useRef<HTMLDivElement | null>(null)

        const handleClose = (e: React.MouseEvent<HTMLButtonElement>): void => {
            e.stopPropagation()
            if (!isDisabled && onDelete) {
                onDelete(e)
            }
        }

        const handleShow = useCallback(() => {
            const isCurrentlyOverflown = isOverflown(textElementRef.current!)
            if (!active) {
                setIsLabelOverflowed(isCurrentlyOverflown)
            }
        }, [label, active])

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLDivElement>): void => {
                if (!isDisabled && onClick) {
                    onClick(e)
                }
                setIsLabelOverflowed(false)
            },
            [isDisabled, onClick],
        )

        useEffect(() => {
            handleShow()
        }, [label])

        return (
            <Tooltip label={isLabelOverflowed ? label : null}>
                <ChipContainer
                    ref={ref}
                    className={className}
                    id={id}
                    tabIndex={isDisabled ? -1 : 0}
                    isDisabled={isDisabled}
                    onClick={handleClick}
                    onMouseEnter={handleShow}
                    active={active}
                    maxWidth={maxWidth}
                    isDeletable={deletable}
                >
                    <div className="content">
                        {icon && (
                            <Icon
                                name={icon}
                                size="20px"
                                color={isDisabled ? 'disable' : active ? 'neutralActionActive' : 'neutralAction'}
                            />
                        )}
                        <Text
                            scale="bodyS500"
                            color={isDisabled ? 'disable' : active ? 'neutralActionActive' : 'neutralAction'}
                            className="label"
                            ref={textElementRef}
                        >
                            {label}
                        </Text>
                    </div>
                    {deletable && (
                        <IconButton
                            onClick={handleClose}
                            isDisabled={isDisabled}
                            size="xs"
                            icon="xCircle"
                            color="neutral"
                            form="icon"
                        />
                    )}
                </ChipContainer>
            </Tooltip>
        )
    },
)

Chip.displayName = 'Chip'

export default Chip
